import { useEffect, useMemo, useState, type FormEvent } from "react";
import { LogOut, Building } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { avatarBgClass, displayNameFor, initialsFor } from "@/lib/avatarColor";
import { toast } from "sonner";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("profile");
  const { t: tAuth } = useTranslation("auth");
  const { id: activeId, memberships, loading } = useCurrentCompany();
  const { isSuperadmin } = useIsSuperadmin();
  const [jobRoles, setJobRoles] = useState<string[]>([]);

  const meta = (user?.user_metadata ?? {}) as { full_name?: string };
  const savedName = meta.full_name ?? "";
  const [fullName, setFullName] = useState(savedName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFullName(savedName);
  }, [savedName]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("user_custom_roles")
      .select("custom_roles:role_id(name)")
      .eq("user_id", user.id)
      .then(({ data }) => {
        if (cancelled) return;
        const names = (data ?? [])
          .map((r) => {
            const rel = (r as { custom_roles?: { name?: string } | { name?: string }[] }).custom_roles;
            return Array.isArray(rel) ? rel[0]?.name : rel?.name;
          })
          .filter((n): n is string => !!n && n.toLowerCase() !== "superadmin")
          .sort((a, b) => a.localeCompare(b));
        setJobRoles(names);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const sortedMemberships = useMemo(
    () =>
      [...memberships].sort((a, b) => {
        if (a.id === activeId) return -1;
        if (b.id === activeId) return 1;
        return (a.name ?? "").localeCompare(b.name ?? "");
      }),
    [memberships, activeId],
  );

  const dirty = fullName.trim() !== savedName;

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: { full_name: fullName.trim() || null },
    });
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success(t("account.saved"));
  };

  const handleSignOut = () => {
    logout();
    navigate("/login");
    toast.success(tAuth("messages.signedOut"));
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">{t("header.title")}</h1>
        <p className="text-muted-foreground">{t("header.subtitle")}</p>
      </div>

      <div className="px-6 space-y-4">
        {/* Account */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-4">
            <div
              className={`w-14 h-14 rounded-full ${avatarBgClass(user?.id ?? null)} flex items-center justify-center text-lg font-semibold text-primary-foreground`}
            >
              {initialsFor(savedName, user?.email)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-semibold text-foreground truncate">
                  {displayNameFor(savedName, user?.email)}
                </p>
              </div>
              {savedName.trim() && (
                <p className="text-sm text-muted-foreground truncate">{user?.email || "—"}</p>
              )}
            </div>
          </div>

          <form onSubmit={handleSave} className="mt-6 grid gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="full_name">{t("account.fullName")}</Label>
              <Input
                id="full_name"
                value={fullName}
                maxLength={100}
                placeholder={t("account.fullNamePlaceholder")}
                onChange={(e) => setFullName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t("account.fullNameHint")}</p>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={!dirty || saving}>
                {saving ? t("account.saving") : t("account.save")}
              </Button>
            </div>
          </form>
        </div>

        {/* Access */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-4 h-4 text-muted-foreground" />
            <h2 className="font-semibold text-foreground">{t("access.title")}</h2>
          </div>
          {isSuperadmin && (
            <div className="pb-3 mb-3 border-b border-border">
              <p className="font-medium text-foreground">{t("superadminBadge")}</p>
              <p className="text-sm text-muted-foreground">{t("access.superadminDescription")}</p>
            </div>
          )}
          {!loading && sortedMemberships.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("access.noCompany")}</p>
          ) : (
            <ul className="divide-y divide-border">
              {sortedMemberships.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-foreground truncate">{m.name ?? "—"}</span>
                    {m.id === activeId && (
                      <Badge variant="outline" className="shrink-0">
                        {t("access.active")}
                      </Badge>
                    )}
                  </div>
                  {!isSuperadmin && (
                    <Badge variant="secondary" className="shrink-0">
                      {m.role === "admin" ? t("access.roles.admin") : t("access.roles.user")}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
          {jobRoles.length > 0 && (
            <p className="text-sm mt-4">
              <span className="text-muted-foreground">{t("access.jobRole")}: </span>
              <span className="font-medium text-foreground">{jobRoles.join(", ")}</span>
            </p>
          )}
          {memberships.length > 0 && (
            <div className="mt-4 space-y-1">
              {memberships.length > 1 && (
                <p className="text-xs text-muted-foreground">{t("access.switchHint")}</p>
              )}
              {!isSuperadmin && <p className="text-xs text-muted-foreground">{t("access.managedBy")}</p>}
            </div>
          )}
        </div>

        {/* Sign out */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Button
            variant="ghost"
            onClick={handleSignOut}
            className="w-full justify-start gap-3 px-4 py-6 text-destructive hover:text-destructive"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">{t("signOut")}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
